import React, { useState, useEffect, useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import uuid from 'uuid-random';
import { LaterSection } from './LaterSection';
import {
  StyledScreen,
  StyledLabel,
  StyledLanguageFeaturesControl,
  StyledSectionLabelsControlWrapper,
  StyledSectionLabelsControl,
  StyledLabelTitle,
  StyledLabelText,
  StyledDefaultScreen,
  StyledControlButton,
  SideWrapper,
  StyledSideWrapper,
  StyledExemplarWrapper,
  ImageWithPositionWrapper,
  StyledArrowBoxShadowTooltip,
} from '../../Styled';
import {
  Section,
  LaterExemplar,
  SectionLabel,
  LanguageFeatureLabel,
  Image,
  ImageSide,
  WritingType,
  ImageDimensions,
  SelectedImage,
  SectionsColors,
  TextTypes,
} from '../../../types';
import { EXEMPLAR_MODES, EXTENDING, PHRASES, PREPOSITIONAL_PHRASES } from '../../../constants';
import { TextPresentationScreen } from './TextPresentationScreen';
import {
  getLanguageFeatureLabels,
  getColorByPartOfSpeech,
  getLaterStateWord,
  getLanguageFeaturesToolMargin,
  getExemplarImageURL,
  getHighlighted,
  getSectionColors,
  computeSectionsColors,
  getStageColors,
  getLanguageFeatureButtonBorder,
} from '../../../utils';
import { StyledWordContext } from '../../../contexts/styledWordContext';
import { ExemplarContext } from '../../../contexts/laterContext';
import { LanguageFeatureTool } from '../../FontTool/LanguageFeatureTool';
import { DrawingPanel } from '../../DrawingPanel';
import { EditImage } from '../../Image';
import { BottomSection } from '../../BottomSection';
import { EditToolContext } from '../../../contexts/editToolContext';
import { ReplaceImageModal } from '../ReplaceImageModal';
import { getHybridLabel } from '../../../utils/exemplar';
import { HybridUnderlineControlButton } from '../../Button';
import { Flex, FlexBehavior, FlexJustify } from 'react-magma-dom';
import { phrasesTooltipContent, phrasesTooltipStyles } from '../../../constants/tooltip';
import { useParams } from '@reach/router';
import { UndoRedoContext } from '../../../contexts/undoRedoContext';

export interface IEarlyScreen {
  exemplarId: number;
  dimensions: {
    width: number;
    height: number;
  };
  mode: string;
  stage: string;
}

export const LaterScreen: React.FC<IEarlyScreen> = (props): JSX.Element => {
  const { exemplarId, dimensions, mode, stage } = props;
  const { screen } = useParams();
  const [isReplaceImageModalOpen, setReplaceImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<SelectedImage>();
  const {
    exemplarState,
    setHighlightedSection,
    setHighlightedLanguage,
    handleFontStyle,
    handleLanguage,
    updateRelationships,
    replaceImage,
    setSavedEdits,
    saveWordLanguageUpdates,
  } = useContext(ExemplarContext);
  const sectionsColors = useMemo<SectionsColors>(
    () => computeSectionsColors(exemplarState.section_labels),
    [exemplarState.section_labels]
  );
  const { styledWordState, setUnderlineHidden, handleTool, handleChangeScreen } = useContext(StyledWordContext);
  const { isEditMode } = useContext(EditToolContext);
  const { isUndoneEventsNotEmpty, clearEvents } = useContext(UndoRedoContext);

  const isImageToolVisible = styledWordState.imageTool.isVisible;
  const isLanguageFeatureToolVisible = styledWordState.tool.isVisible;
  const isUnderlineTextHidden = styledWordState.isUnderlineHiddenMode;
  const isTextPresentationMode = mode === EXEMPLAR_MODES.textPresentation;
  const isLanguageFeaturesMode = mode === EXEMPLAR_MODES.languageFeatures;
  const isTextStructureMode = mode === EXEMPLAR_MODES.textStructure;
  const isHybridTextType = exemplarState.text_type == TextTypes.hybrid;
  const isHybridUnderlinedControlButton =
    exemplarState.additional_text_types && isHybridTextType && isTextPresentationMode;
  const isLanguageToolVisible = isEditMode && isLanguageFeaturesMode && isLanguageFeatureToolVisible;

  const highlighted = getHighlighted(exemplarState, mode);
  const setHighlighted = isTextStructureMode ? setHighlightedSection : setHighlightedLanguage;

  useEffect(() => {
    if (isUndoneEventsNotEmpty) {
      clearEvents();
    }

    if (!isTextPresentationMode) {
      setHighlighted(
        getHighlighted(
          {
            ...exemplarState,
            meta: { ...exemplarState.meta, highlightedSectionLabels: {}, highlightedLanguageFeatureLabels: {} },
          },
          mode
        )
      );
    }
  }, [mode]);

  useEffect(() => {
    handleChangeScreen(styledWordState);

    if (!isTextPresentationMode) {
      setHighlighted(getHighlighted(exemplarState, mode));
    }
  }, [screen]);

  useEffect(() => {
    setSavedEdits(exemplarState, mode);
  }, [exemplarState, mode]);

  const handleUploadImage = (
    id: string,
    default_uuid: string | undefined,
    sectionId: string,
    dimensions: ImageDimensions,
    side?: ImageSide
  ) => {
    setSelectedImage({
      id,
      default_uuid,
      sectionId,
      side,
      dimensions,
    });
    setReplaceImageModal(true);
  };

  const onControlClick = (setting: string) => {
    setHighlighted({
      ...highlighted,
      [setting]: !highlighted[setting],
    });
  };

  const onControlKeyDown = (event: React.KeyboardEvent, setting: string) => {
    const { key } = event;

    if (key == 'Enter' || key === ' ') {
      onControlClick(setting);
    }
  };

  const renderContent = (sections: Array<Section | Image>, side: ImageSide) => {
    return sections.map((section: Section | Image, index) => {
      if ('lines' in section) {
        return (
          <LaterSection
            key={section.id}
            section={section}
            sectionIndex={index}
            editMode={isEditMode}
            hidden={false}
            highlighted={highlighted}
            mode={mode}
            colors={getSectionColors(section.label, sectionsColors)}
            side={side}
            textType={exemplarState.text_type}
            isAccessible={!isImageToolVisible}
            stage={stage}
            handleFontStyle={handleFontStyle}
          />
        );
      }

      if (isTextPresentationMode) {
        const editImage = (hasPosition: boolean) => {
          return (
            <EditImage
              editMode={isEditMode && section.editable}
              side={side}
              src={getExemplarImageURL(section.uuid, exemplarState.relationships.images)}
              uuid={section.uuid}
              default_uuid={section.default_uuid}
              alt={`${stage} image`}
              styledWordState={styledWordState}
              uploadImage={handleUploadImage}
              hasPosition={hasPosition}
            />
          );
        };

        if (section.position) {
          const { x, y, width, height } = section.position;
          const MAX_PROPORTIONAL_IMAGE_WIDTH = 49;

          return (
            <ImageWithPositionWrapper
              width={width > MAX_PROPORTIONAL_IMAGE_WIDTH ? MAX_PROPORTIONAL_IMAGE_WIDTH : width}
              top={y}
              left={x}
              height={height}
              key={index}
            >
              {editImage(true)}
            </ImageWithPositionWrapper>
          );
        }

        return editImage(false);
      }
    });
  };

  const renderControls = (exemplar: LaterExemplar) => {
    const { section_labels, language_feature_labels } = exemplar;

    if (isTextStructureMode) {
      return (
        <StyledSectionLabelsControlWrapper>
          <StyledSectionLabelsControl>
            {section_labels.map((section_label: SectionLabel, index: number) => {
              const { label, description } = section_label;
              const colors = getSectionColors(label, sectionsColors);

              return (
                <StyledLabel
                  key={index}
                  onClick={() => onControlClick(label)}
                  onKeyDown={(event: React.KeyboardEvent) => onControlKeyDown(event, label)}
                  highlight={highlighted[label]}
                  role="button"
                  tabIndex={0}
                >
                  <StyledLabelTitle color={colors.title}>{label}</StyledLabelTitle>
                  <StyledLabelText hasText={description != ''} color={colors.text}>
                    {description}
                  </StyledLabelText>
                </StyledLabel>
              );
            })}
          </StyledSectionLabelsControl>
        </StyledSectionLabelsControlWrapper>
      );
    } else if (isLanguageFeaturesMode) {
      return (
        <StyledLanguageFeaturesControl>
          {language_feature_labels.map((language_feature_label: LanguageFeatureLabel, index: number) => {
            const { label } = language_feature_label;
            const margin = getLanguageFeaturesToolMargin(language_feature_labels, index);
            const name = getLanguageFeatureLabels(label);
            const color = getColorByPartOfSpeech(label);
            const isDisabled = styledWordState.isHiddenTextMode;
            const isSelected = highlighted[label];
            const isPhrases = label === PHRASES;
            const isPrepositionalPhrases = label === PREPOSITIONAL_PHRASES;
            const btnColor = (isPrepositionalPhrases || isPhrases) && isSelected ? 'transparent' : color;

            const languageFeatureControlButton = (
              <Flex behavior={FlexBehavior.container}>
                <StyledControlButton
                  key={index}
                  testId="lf-control-button"
                  margin={margin}
                  disabled={isDisabled}
                  color={btnColor}
                  onClick={() => onControlClick(label)}
                  withOpacity={isSelected}
                  border={getLanguageFeatureButtonBorder(label)}
                >
                  <ReactMarkdown skipHtml={false}>{name}</ReactMarkdown>
                </StyledControlButton>
              </Flex>
            );

            return isPhrases ? (
              <StyledArrowBoxShadowTooltip
                testId="phrases-tooltip"
                isInverse
                tooltipStyle={phrasesTooltipStyles}
                content={phrasesTooltipContent}
              >
                {languageFeatureControlButton}
              </StyledArrowBoxShadowTooltip>
            ) : (
              languageFeatureControlButton
            );
          })}
        </StyledLanguageFeaturesControl>
      );
    }
    return null;
  };

  const renderBottomSection = () => {
    const { bottom } = exemplarState.content;

    const imagesWithURL =
      bottom &&
      bottom.map(image => {
        return { ...image, url: getExemplarImageURL(image.uuid, exemplarState.relationships.images) };
      });

    return (
      <>
        {imagesWithURL && (
          <BottomSection>
            {imagesWithURL.map(image => {
              return (
                <EditImage
                  key={image.uuid}
                  editMode={isEditMode}
                  side={ImageSide.bottom}
                  src={getExemplarImageURL(image.uuid, exemplarState.relationships.images)}
                  uuid={image.uuid}
                  default_uuid={image.default_uuid}
                  alt={`${stage} image`}
                  styledWordState={styledWordState}
                  uploadImage={handleUploadImage}
                  hasPosition={false}
                />
              );
            })}
          </BottomSection>
        )}
      </>
    );
  };

  const renderLater = () => {
    if (isTextStructureMode) {
      const { left: leftContent, right: rightContent } = exemplarState.content;

      return (
        <StyledExemplarWrapper>
          <StyledScreen tabIndex={0}>
            <SideWrapper>{renderContent(leftContent as Section[], ImageSide.left)}</SideWrapper>
            <SideWrapper>
              <LaterSection
                editMode={isEditMode}
                hidden={true}
                section={leftContent[0] as Section}
                key={uuid()}
                highlighted={highlighted}
                mode={mode}
                textType={exemplarState.text_type}
                stage={stage}
                handleFontStyle={handleFontStyle}
              />
              {renderContent(rightContent as Section[], ImageSide.right)}
            </SideWrapper>
          </StyledScreen>
          {renderControls(exemplarState)}
        </StyledExemplarWrapper>
      );
    }

    return (
      <StyledExemplarWrapper padding="0px 32px 0 39px">
        <StyledDefaultScreen isPresentationMode={isTextPresentationMode} tabIndex={0}>
          <StyledSideWrapper isExtending={stage === EXTENDING}>
            {renderContent(exemplarState.content.left, ImageSide.left)}
          </StyledSideWrapper>
          <StyledSideWrapper>
            <LaterSection
              editMode={isEditMode}
              hidden={true}
              section={exemplarState.content.left[0] as Section}
              key={uuid()}
              highlighted={highlighted}
              mode={mode}
              textType={exemplarState.text_type}
              stage={stage}
              handleFontStyle={handleFontStyle}
            />
            {renderContent(exemplarState.content.right, ImageSide.right)}
            <Flex testId="underline-control-wrapper" behavior={FlexBehavior.container} justify={FlexJustify.flexEnd}>
              {isHybridUnderlinedControlButton && (
                <HybridUnderlineControlButton
                  isUnderlineTextHidden={isUnderlineTextHidden}
                  onClick={() => setUnderlineHidden(!isUnderlineTextHidden)}
                >
                  {getHybridLabel(exemplarState)}
                </HybridUnderlineControlButton>
              )}
            </Flex>
          </StyledSideWrapper>
        </StyledDefaultScreen>
        {renderControls(exemplarState)}
        {isTextPresentationMode && renderBottomSection()}
      </StyledExemplarWrapper>
    );
  };

  return (
    <>
      <TextPresentationScreen editMode={isEditMode} stage={stage} />
      {renderLater()}
      <DrawingPanel mode={mode} isAccessible={!isImageToolVisible} exemplarDimensions={dimensions} />
      {isLanguageToolVisible && (
        <LanguageFeatureTool
          tool={styledWordState.tool}
          exemplarWord={getLaterStateWord(exemplarState, styledWordState.tool.word)}
          languageFeatureLabels={exemplarState.language_feature_labels}
          handleTool={handleTool}
          handleFontStyle={handleFontStyle}
          handleLanguage={handleLanguage}
          saveWordLanguageUpdates={saveWordLanguageUpdates}
        />
      )}
      {isReplaceImageModalOpen && selectedImage && (
        <ReplaceImageModal
          exemplarId={exemplarId}
          default_uuid={selectedImage.default_uuid}
          currentImageUUID={selectedImage.id}
          mode={mode}
          side={selectedImage.side}
          sectionId={selectedImage.sectionId}
          defaultImageDimensions={selectedImage.dimensions}
          relatedImages={exemplarState.relationships.images}
          writingType={WritingType.exemplars}
          color={getStageColors(stage).main}
          setRelationships={updateRelationships}
          replaceImage={replaceImage}
          onClose={() => setReplaceImageModal(false)}
        />
      )}
    </>
  );
};
